import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import envConfig from '~/configs/env'
import type { PreferredLanguage } from '~/data/types/preferredUnits'

import type { PartialStore } from '../types'
import { dispatcherFactory } from '../utils'

export type SideDrawerId = 'schedule' | 'notes'

type DisplayStore = PartialStore<typeof displaySlice>
type DisplayState = {
  snackbar: SnackbarState
  bannerOffset: number
  /** Which content-shifting side drawer is open, if any. Mirrored from the URL `drawer` param. */
  activeSideDrawer: SideDrawerId | null
  preferredLanguage: PreferredLanguage
}

type OpenSnackbarPayload = {
  message: string
  severity?: SnackbarSeverity
  title?: string
}

type SnackbarSeverity = 'success' | 'info' | 'warning' | 'error'
type SnackbarState = {
  isOpen: boolean
  message: string
  severity?: SnackbarSeverity
  title?: string
}

const initialState: DisplayState = {
  snackbar: {
    isOpen: false,
    message: ''
  },
  bannerOffset: 0,
  activeSideDrawer: null,
  preferredLanguage: envConfig.defaultLanguage
}

const displaySlice = createSlice({
  name: 'display',
  initialState,
  reducers: {
    closeSnackbar: (state) => {
      // Retain the message, title and severity so the snackbar doesn't change appearance while it fades out
      state.snackbar.isOpen = false
    },
    openSnackbar: (state, action: PayloadAction<OpenSnackbarPayload>) => {
      state.snackbar = {
        isOpen: true,
        message: action.payload.message,
        severity: action.payload.severity ?? 'success',
        title: action.payload.title
      }
    },
    setBannerOffset: (state, action: PayloadAction<number>) => {
      state.bannerOffset = action.payload
    },
    setActiveSideDrawer: (state, action: PayloadAction<SideDrawerId | null>) => {
      state.activeSideDrawer = action.payload
    },
    setPreferredLanguage: (state, action: PayloadAction<PreferredLanguage>) => {
      state.preferredLanguage = action.payload
    }
  }
})

const { setActiveSideDrawer } = displaySlice.actions
export default displaySlice.reducer

const selectSnackbar = (state: DisplayStore) => state.display.snackbar
const selectBannerOffset = (state: DisplayStore) => state.display.bannerOffset
const selectPreferredLanguage = (state: DisplayStore) => state.display.preferredLanguage
const selectActiveSideDrawer = (state: DisplayStore) => state.display.activeSideDrawer

export const useSnackbar = () => useSelector(selectSnackbar)
export const useBannerOffset = () => useSelector(selectBannerOffset)
export const usePreferredLanguage = () => useSelector(selectPreferredLanguage)
// Provides easy access to dispatching actions
export type OpenSnackbarOptions = { severity?: SnackbarSeverity; title?: string }

export const useSnackbarControls = () => {
  const dispatch = useDispatch()

  const openSnackbar = useCallback(
    (message: string, options: OpenSnackbarOptions = {}) =>
      dispatch(displaySlice.actions.openSnackbar({ message, ...options })),
    [dispatch]
  )

  const closeSnackbar = useCallback(() => dispatch(displaySlice.actions.closeSnackbar()), [dispatch])

  return {
    closeSnackbar,
    openSnackbar
  }
}

export function useLongRunningSnackbar(timeoutSeconds: number = 11) {
  const { openSnackbar } = useSnackbarControls()
  const [shouldShowLongRunningSnackbar, setShouldShowLongRunningSnackbar] = useState(false)
  const [triggerLongRunningSnackbar, setTriggerLongRunningSnackbar] = useState(false)

  useEffect(() => {
    if (shouldShowLongRunningSnackbar) {
      setTimeout(() => {
        setTriggerLongRunningSnackbar(true)
      }, 1000 * timeoutSeconds)
    }
  }, [shouldShowLongRunningSnackbar, timeoutSeconds])

  useEffect(() => {
    if (triggerLongRunningSnackbar && shouldShowLongRunningSnackbar) {
      openSnackbar('Refresh is taking awhile, check back in a few minutes.', {
        severity: 'info'
      })
    }
    if (triggerLongRunningSnackbar) setShouldShowLongRunningSnackbar(false)
    setTriggerLongRunningSnackbar(false)
  }, [triggerLongRunningSnackbar, openSnackbar, shouldShowLongRunningSnackbar])

  return { setShouldShowLongRunningSnackbar }
}

export const useBannerOffsetControls = () => {
  const dispatch = useDispatch()

  return useCallback(
    (offset: number) => {
      dispatch(displaySlice.actions.setBannerOffset(offset))
    },
    [dispatch]
  )
}

export const useSideDrawerControls = () => {
  const dispatch = useDispatch()
  const activeDrawer = useSelector(selectActiveSideDrawer)
  const isOpen = activeDrawer !== null

  const openDrawer = useCallback((id: SideDrawerId) => dispatch(setActiveSideDrawer(id)), [dispatch])
  const closeDrawer = useCallback(() => dispatch(setActiveSideDrawer(null)), [dispatch])
  const isDrawerOpen = useCallback((id: SideDrawerId) => activeDrawer === id, [activeDrawer])

  return {
    activeDrawer,
    isOpen,
    isDrawerOpen,
    openDrawer,
    closeDrawer
  }
}
export const { setPreferredLanguage } = displaySlice.actions
export const useSetPreferredLanguage = dispatcherFactory(setPreferredLanguage)

// These locales map to the Auth0's ui_locales param.
const localeMap = { en: 'en-US', fr: 'fr-FR', de: 'de' }
export function useLocale() {
  const language = usePreferredLanguage()
  return localeMap[language]
}
